import React from 'react';
import { KTSVG } from '../../../../_metronic/helpers';
import { SortField, SortOrder, ViewMode, StatusFilter } from '../utils/customerListTypes';
import StatusFilterDropdown from './StatusFilterDropdown';

export interface CustomerListToolbarProps {
  searchTerm: string;
  onSearchChange: (value: string) => void;
  statusFilter: StatusFilter;
  onStatusFilterChange: (value: StatusFilter) => void;
  statusFilterOpen: boolean;
  onStatusFilterToggle: () => void;
  onStatusFilterClose: () => void;
  sortField: SortField;
  sortOrder: SortOrder;
  onSortFieldChange: (value: SortField) => void;
  onSortOrderChange: (value: SortOrder) => void;
  viewMode: ViewMode;
  onViewModeChange: (value: ViewMode) => void;
  customerCount: number;
  lastContactedError: string | null;
  onRefresh: () => void;
  refreshing: boolean;
  onCalendarOpen: () => void;
  onClearFilters?: () => void;
}

const CustomerListToolbar: React.FC<CustomerListToolbarProps> = ({
  searchTerm,
  onSearchChange,
  statusFilter,
  onStatusFilterChange,
  statusFilterOpen,
  onStatusFilterToggle,
  onStatusFilterClose,
  sortField,
  sortOrder,
  onSortFieldChange,
  onSortOrderChange,
  viewMode,
  onViewModeChange,
  customerCount,
  lastContactedError,
  onRefresh,
  refreshing,
  onCalendarOpen,
}) => {
  return (
    <div className="card-header border-0 pt-6 pb-2">
      {/* Title Section */}
      <div className="w-100 mb-4">
        <h3 className="card-label fw-bold fs-3 mb-1">Active Customers</h3>
        <span className="text-muted fw-semibold fs-7">
          Manage customers with active jobs ({customerCount} showing, {statusFilter.length} status{statusFilter.length !== 1 ? 'es' : ''} selected)
        </span>
      </div>

      {/* Error Display */}
      {lastContactedError && (
        <div className="w-100 mb-4">
          <div className="alert alert-danger p-2 fs-8" role="alert">
            {lastContactedError}
          </div>
        </div>
      )}

      {/* Filters Section - Full Width */}
      <div className="w-100 mb-4">
        <div className="row g-3">
          {/* Search */}
          <div className="col-lg-4 col-md-6">
            <div className="position-relative">
              <KTSVG
                path="/media/icons/duotune/general/gen021.svg"
                className="svg-icon-3 position-absolute ms-3 mt-3"
              />
              <input
                type="text"
                className="form-control form-control-solid ps-12"
                placeholder="Search customers..."
                value={searchTerm}
                onChange={(e) => onSearchChange(e.target.value)}
              />
            </div>
          </div>

          {/* Status Filter */}
          <div className="col-lg-3 col-md-6">
            <StatusFilterDropdown
              statusFilter={statusFilter}
              onStatusFilterChange={onStatusFilterChange}
              open={statusFilterOpen}
              onToggle={onStatusFilterToggle}
              onClose={onStatusFilterClose}
            />
          </div>

          {/* Sort Field */}
          <div className="col-lg-2 col-md-6">
            <select
              className="form-select form-select-solid"
              value={sortField}
              onChange={(e) => onSortFieldChange(e.target.value as SortField)}
            >
              <option value="name">Sort by Name</option>
              <option value="lastContacted">Sort by Last Contact</option>
              <option value="dueDate">Sort by Due Date</option>
              <option value="status">Sort by Status</option>
            </select>
          </div>

          {/* Sort Order */}
          <div className="col-lg-3 col-md-6">
            <select
              className="form-select form-select-solid"
              value={sortOrder}
              onChange={(e) => onSortOrderChange(e.target.value as SortOrder)}
            >
              <option value="asc">Ascending</option>
              <option value="desc">Descending</option>
            </select>
          </div>
        </div>
      </div>

      {/* Action Buttons Section */}
      <div className="w-100">
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-3">
          {/* Left Side - View Controls */}
          <div className="d-flex align-items-center gap-2">
            <div className="btn-group" role="group">
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'grid' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => onViewModeChange('grid')}
                title="Grid View"
              >
                <KTSVG path="/media/icons/duotune/general/gen054.svg" className="svg-icon-5" />
              </button>
              <button
                type="button"
                className={`btn btn-sm ${viewMode === 'list' ? 'btn-primary' : 'btn-light-primary'}`}
                onClick={() => onViewModeChange('list')}
                title="List View"
              >
                <KTSVG path="/media/icons/duotune/abstract/abs015.svg" className="svg-icon-5" />
              </button>
            </div>
          </div>

          {/* Right Side - Action Buttons */}
          <div className="d-flex align-items-center gap-2 flex-wrap">
            <button
              className="btn btn-light-info btn-sm"
              onClick={onCalendarOpen}
              title="Open Calendar"
            >
              <KTSVG path="/media/icons/duotune/general/gen014.svg" className="svg-icon-3" />
              Calendar
            </button>
            <button
              className="btn btn-light-primary btn-sm"
              onClick={onRefresh}
              disabled={refreshing}
            >
              {refreshing ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Refreshing...
                </>
              ) : (
                <>
                  <KTSVG path="/media/icons/duotune/communication/com004.svg" className="svg-icon-3" />
                  Refresh
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CustomerListToolbar;
